import { type PointerEventHandler, useEffect, useRef } from 'react';

import { filterNullishValuesFromObject } from '@pkg/utils';

// Consider a generic so this can be appied to other elements.
export type ClickHoldRepeatCallback = PointerEventHandler<HTMLButtonElement>;

export interface ClickHoldRepeatOptions {
    disabled?: boolean;
    // Restrict `callback` to only the "left" click/tap. This could be
    // handled within the `callback > event`, but doing the early return
    // here is preferred to avoid a "stuck right-click".
    leftPointerOnly?: boolean;
    // Useful to "skip" when also paired with a `onClick/onAction`.
    // Avoids the immediate "double-execution".
    skipImmediateExecution?: boolean;
}

const DEFAULT_OPTIONS: Required<ClickHoldRepeatOptions> = {
    disabled: false,
    leftPointerOnly: false,
    skipImmediateExecution: false,
};

// Callbacks are arranged in a "timed waterfall" so that it feels like
// we "exponentially ramp up" to a faster increment/decrement.
// The first instance of `callback` will be executed immediately.
// The next `callback` will occur `400ms` later, followed by another
// execution `200ms` after that. Finally, the `callback` will repeat
// every `60ms` until it is stopped.
export function useClickHoldRepeat(callback: ClickHoldRepeatCallback, options?: ClickHoldRepeatOptions) {
    const { disabled, leftPointerOnly, skipImmediateExecution } = {
        ...DEFAULT_OPTIONS,
        ...filterNullishValuesFromObject<ClickHoldRepeatOptions>(options ?? {}),
    };

    const timeout1Ref = useRef<number>();
    const timeout2Ref = useRef<number>();
    const intervalRef = useRef<number>();

    const handleAction: ClickHoldRepeatCallback = (event) => {
        const leftPointerEvent = event.button === 0;

        if (disabled || (leftPointerOnly && !leftPointerEvent)) return;

        if (!skipImmediateExecution) callback(event);

        timeout1Ref.current = window.setTimeout(() => {
            callback(event);

            timeout2Ref.current = window.setTimeout(() => {
                intervalRef.current = window.setInterval(callback, 60);
            }, 200);
        }, 400);
    };

    function handleReset() {
        window.clearTimeout(timeout1Ref.current);
        timeout1Ref.current = undefined;

        window.clearTimeout(timeout2Ref.current);
        timeout2Ref.current = undefined;

        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
    }

    useEffect(() => {
        if (disabled) handleReset();

        () => {
            handleReset();
        };
    }, [disabled]);

    return {
        active: Boolean(timeout1Ref.current),
        handleAction,
        handleReset,
    };
}
